import * as amplitude from '@amplitude/analytics-browser';
import { ORDER_QUERY_KEY } from '@app/services/hooks/orders/useOrderDetails';
import { buildTransactionData, pushRawGTMEvent } from '@app/utils/analytics';
import i18next from 'i18next';
import { uniq } from 'ramda';
import { push } from 'redux-first-history';
import { combineEpics } from 'redux-observable';
import { from, merge } from 'rxjs';
import { filter, ignoreElements, map, mergeAll, mergeMap, tap } from 'rxjs/operators';
import { isActionOf } from 'typesafe-actions';

import { addToast } from '../components/various/Toasts';
import {
  addProductsToOrder,
  addProductsToOrderFailure,
  addProductsToOrderSuccess,
  addVoucherFailure,
  addVoucherRequest,
  addVoucherSuccess,
  checkoutOrderSplitSuccess,
  checkoutOrderSuccess,
  copyToNewSelectionFailure,
  copyToNewSelectionRequest,
  copyToNewSelectionSuccess,
  createSelection,
  fetchCountriesRequest,
  fetchOrderSuccess,
  getIsSeller,
  getOrderDetails,
  getOrderId,
  hideModal,
  loadTermsFailure,
  loadTermsSuccess,
  removeProductsFromOrder,
  removeProductsFromOrderFailure,
  removeProductsFromOrderSuccess,
  removeVoucherFailure,
  removeVoucherRequest,
  removeVoucherSuccess,
  resetCheckoutForm,
  setDeliveryWindows,
  setItemsRequest,
  setMultipleItemsRequest,
  sharedSelectionFailure,
  sharedSelectionRequest,
  sharedSelectionSuccess,
  showTerms,
  updateCheckoutForm,
} from '../ducks';
import { compiledPaths, paths } from '../paths';
import { ORDERS_QUERY_KEY } from '../services/hooks/orders/useOrdersList';
import { getError } from '../utils/errors';
import { handleResponseStatus } from '../utils/handleResponseStatus';
import { isDefined } from '../utils/is';
import { omit } from '../utils/omit';
import { mapResponse } from '../utils/operators/mapResponse';

const pushTransactionDataEpic: AppEpic = (action$, store$) =>
  merge(
    action$.pipe(
      filter(isActionOf(checkoutOrderSplitSuccess)),
      map(action => action.payload),
    ),
    action$.pipe(
      filter(isActionOf(checkoutOrderSuccess)),
      map(action => action.payload),
    ),
  ).pipe(
    map(({ orders }) => buildTransactionData(orders, getOrderDetails(store$.value))),
    filter(isDefined),
    mergeAll(),
    tap(pushRawGTMEvent),
    ignoreElements(),
  );

const copyToNewSelectionEpic: AppEpic = (action$, _, { ordersRepository }) =>
  action$.pipe(
    filter(isActionOf(copyToNewSelectionRequest)),
    map(action => action.payload),
    mergeMap(data =>
      from(ordersRepository.copyToNewSelectionRequest(data)).pipe(
        mapResponse(
          ({ data: { newOrderId, failedProducts, products } }) => {
            const { orderNumber } = data;
            addToast(i18next.t('selections:order_copied_hint', { orderNumber }), {
              description: i18next.t('selections:not_empty_order_copied_hint', { orderNumber }),
              duration: 10000,
            });

            return [
              copyToNewSelectionSuccess({
                failedProducts: {
                  failedProducts,
                  products,
                },
              }),
              hideModal('CopyToNewSelectionModal'),
              push({ pathname: compiledPaths.ORDER_DETAILS({ id: newOrderId }) }),
            ];
          },
          () => {
            addToast(i18next.t('selections:copy_selection_fail'));

            return copyToNewSelectionFailure();
          },
        ),
      ),
    ),
  );

const resetCheckoutFormEpic: AppEpic = action$ =>
  action$.pipe(
    filter(isActionOf([checkoutOrderSuccess, checkoutOrderSplitSuccess, copyToNewSelectionRequest, createSelection])),
    map(() => resetCheckoutForm()),
  );

const resetTermsInCheckoutFormEpic: AppEpic = (action$, state$) => {
  return action$.pipe(
    filter(isActionOf([addProductsToOrder, setItemsRequest, setMultipleItemsRequest])),
    filter(() => isDefined(state$.value.order.checkoutForm) && !getIsSeller(state$.value)),
    map(() => updateCheckoutForm({ ...state$.value.order.checkoutForm, isTermsAndConditionsChecked: false })),
  );
};

const verifySharedSelectionEpic: AppEpic = (action$, _, { selectionRepository, queryClient }) =>
  action$.pipe(
    filter(isActionOf(sharedSelectionRequest)),
    map(action => action.payload),
    mergeMap(async ({ orderId }) => {
      const response = await selectionRepository.getOrderFromLink(orderId);

      return { orderId, response };
    }),
    mergeMap(({ response, orderId }) =>
      handleResponseStatus(response)(
        ({ data }) => {
          const mixedDeliveryWindows = data.deliveryWindows ?? [];
          queryClient.setQueryData([ORDER_QUERY_KEY, orderId], data);

          return [
            sharedSelectionSuccess(data),
            fetchOrderSuccess(omit(data, ['token'])),
            setDeliveryWindows(mixedDeliveryWindows),
            fetchCountriesRequest(),
          ];
        },
        () => {
          return [push({ pathname: paths.LINK_EXPIRED }), sharedSelectionFailure()];
        },
      ),
    ),
  );

const addProductsToOrderEpic: AppEpic = (action$, store$, { ordersRepository }) =>
  action$.pipe(
    filter(isActionOf(addProductsToOrder)),
    map(action => action.payload),
    map(({ isAddingByBarcode, products, deliveryWindows }) => ({
      deliveryWindows,
      isAddingByBarcode,
      orderId: getOrderId(store$.value),
      products,
    })),
    mergeMap(({ isAddingByBarcode, orderId, products, deliveryWindows }) =>
      from(ordersRepository.addProductsToOrder(orderId, { deliveryWindows, products })).pipe(
        mapResponse(
          res => addProductsToOrderSuccess({ data: res.data, isAddingByBarcode }),
          () => addProductsToOrderFailure({ isAddingByBarcode, products }),
        ),
      ),
    ),
  );

const removeProductsFromOrderEpic: AppEpic = (action$, store$, { ordersRepository }) =>
  action$.pipe(
    filter(isActionOf(removeProductsFromOrder)),
    map(action => action.payload),
    map(({ products, deliveryWindows, variants }) => ({
      orderId: getOrderId(store$.value),
      payload: {
        ...(isDefined(deliveryWindows) && { deliveryWindows }),
        ...(isDefined(variants) && { variants }),
        products,
      },
    })),
    mergeMap(({ orderId, payload }) =>
      from(ordersRepository.removeProductsFromOrder(orderId, payload)).pipe(
        mapResponse(
          res => {
            const removedProductsCount = uniq(res.data.variantsRemoved.map(({ product }) => product)).length;

            addToast(i18next.t('selections:product_removed_from_selection', { count: removedProductsCount }));

            return removeProductsFromOrderSuccess(res.data);
          },
          () => {
            addToast(i18next.t('products:remove_products_fail'));

            return removeProductsFromOrderFailure({ products: payload.products });
          },
        ),
      ),
    ),
  );

const addVoucherEpic: AppEpic = (action$, store$, { ordersRepository }) =>
  action$.pipe(
    filter(isActionOf(addVoucherRequest)),
    map(action => action.payload),
    map(voucher => ({
      orderId: getOrderId(store$.value),
      voucher,
    })),
    mergeMap(async ({ orderId, voucher }) => {
      amplitude.track('checkoutPage.button.addVoucher.click');

      return ordersRepository.addVoucher(orderId, voucher);
    }),
    mapResponse(
      res => {
        amplitude.track('checkout.addVoucher.success');

        return addVoucherSuccess(res.data);
      },
      error => {
        const voucherError = getError(error);
        amplitude.track('checkout.addVoucher.error', { error: voucherError });

        return addVoucherFailure(voucherError);
      },
    ),
  );

const removeVoucherEpic: AppEpic = (action$, store$, { ordersRepository }) =>
  action$.pipe(
    filter(isActionOf(removeVoucherRequest)),
    map(action => action.payload),
    map(voucher => ({
      orderId: getOrderId(store$.value),
      voucher,
    })),
    mergeMap(async ({ orderId, voucher }) => ordersRepository.removeVoucher(orderId, voucher)),
    mapResponse(
      res => removeVoucherSuccess(res.data),
      () => removeVoucherFailure(),
    ),
  );

const showTermsAndConditionsEpic: AppEpic = (action$, _, { ordersRepository }) =>
  action$.pipe(
    filter(isActionOf(showTerms)),
    mergeMap(async () => ordersRepository.getTermsAndConditions()),
    mapResponse(
      res => loadTermsSuccess(res.data),
      () => loadTermsFailure(),
    ),
  );

const invalidateOrdersListEpic: AppEpic = (action$, _, { queryClient }) =>
  action$.pipe(
    filter(isActionOf([checkoutOrderSuccess, checkoutOrderSplitSuccess])),
    tap(async () => queryClient.resetQueries({ queryKey: [ORDERS_QUERY_KEY] })),
    ignoreElements(),
  );

export const orderEpic = combineEpics(
  pushTransactionDataEpic,
  copyToNewSelectionEpic,
  addProductsToOrderEpic,
  removeProductsFromOrderEpic,
  addVoucherEpic,
  removeVoucherEpic,
  showTermsAndConditionsEpic,
  verifySharedSelectionEpic,
  invalidateOrdersListEpic,
  resetCheckoutFormEpic,
  resetTermsInCheckoutFormEpic,
);
